/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { FiCheck, FiChevronDown } from 'react-icons/fi'

export interface GeofenceFilterOption {
  value: string
  label: string
}

interface GeofenceFilterDropdownProps {
  label: string
  options: GeofenceFilterOption[]
  selected: string[]
  onChange: (next: string[]) => void
  multiple?: boolean
  minWidth?: string
}

export const GeofenceFilterDropdown = ({
  label,
  options,
  selected,
  onChange,
  multiple = true,
  minWidth = 'min-w-[140px]',
}: GeofenceFilterDropdownProps) => {
  const selectedLabel = multiple
    ? selected.length > 0
      ? selected.length + ' selected'
      : 'All'
    : (options.find((option) => option.value === selected[0])?.label ?? 'All')

  const toggle = (value: string) => {
    if (!multiple) {
      onChange([value])
      return
    }
    onChange(selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value])
  }

  return (
    <div className="dropdown" data-filter-dropdown>
      <div
        tabIndex={0}
        role="button"
        className={'btn btn-sm btn-outline ' + minWidth + ' flex items-center justify-between font-normal'}
      >
        <span className="truncate">
          {label}: {selectedLabel}
        </span>
        <FiChevronDown className="h-4 w-4 shrink-0 text-base-content/60" />
      </div>
      <ul
        tabIndex={0}
        className="dropdown-content menu z-20 mt-1 box-border w-60 min-w-0 max-w-[calc(100vw-1rem)] overflow-x-hidden rounded-lg border border-base-300 bg-base-100 p-1 shadow-lg"
      >
        {options.map((option) => {
          const checked = selected.includes(option.value)
          return (
            <li key={option.value} className="min-w-0 max-w-full overflow-hidden">
              <button
                type="button"
                onClick={() => toggle(option.value)}
                className={
                  'flex w-full min-w-0 max-w-full items-center gap-2 overflow-hidden text-xs ' +
                  (checked ? 'active' : '')
                }
              >
                {multiple && (
                  <span
                    aria-hidden
                    className={
                      'flex h-4 w-4 items-center justify-center rounded-[3px] border ' +
                      (checked ? 'border-primary bg-primary text-primary-content' : 'border-base-content/40')
                    }
                  >
                    {checked && <FiCheck className="text-[11px]" />}
                  </span>
                )}
                <span className="min-w-0 flex-1 truncate text-left">{option.label}</span>
              </button>
            </li>
          )
        })}
        {multiple && (
          <li className="mt-1 min-w-0 max-w-full overflow-hidden border-t border-base-200 pt-1">
            <button type="button" onClick={() => onChange([])} className="w-full min-w-0 text-left text-xs text-accent">
              Clear
            </button>
          </li>
        )}
      </ul>
    </div>
  )
}
